import { Model, type ModelProps } from '@/infra/model'
import { computed, observable } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import LegendSettings, {
  type LegendSettingsProps,
} from '@/features/jobs/legends/LegendSettings'
import type { JobStatus } from '@/features/jobs/job-status'
import { Id } from '@/infra/id'
import {
  JobProgression,
  type ProgressionStepStatus,
} from '@/features/jobs/taxes/progression/JobProgression'
import {
  computeProgressionFlow,
  getCurrentStepContent,
} from '@/features/jobs/taxes/progression/progression-content'
import type { ConsultationStatus } from '@/features/jobs/consultations/Consultation'
import type { TimeRange } from '@/infra/time-range'
import type { BookkeepingStage } from '@/features/jobs/bookkeeping/bookkeeping-stage'
import type { CollaboratorProps } from '@/features/jobs/details/SubmittedJob'
import type { BookkeepingCycle } from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import type { BookkeepingAccessStatus } from '@/features/jobs/bookkeeping/bookkeeping-access-status'
import type { BookkeepingDisplayStage } from '@/features/jobs/bookkeeping/bookkeeping-display-stage'
import type { JobScopeChangeStatus } from '@/features/jobs/job-scope-changes/JobScopeChange'

export type SearchableJobProps = ModelProps<SearchableJob>

/**
 *  The searchable consultation.
 */
export interface SearchableConsultation {
  id: Id
  status: ConsultationStatus
  bookedTime?: TimeRange
  providerUserPublicId?: string
  roomId?: Id
}

export const SearchableConsultation = {
  default: {
    id: Id.none,
    status: 'Unknown',
  } as SearchableConsultation,
}

/**
 *  The searchable work item.
 */
export interface SearchableWorkItem {
  id: Id
  quantity: number
  completedCount: number
}

/**
 *  The searchable bookkeeping progression info.
 */
export interface SearchableBookkeepingProgressionInfo {
  id: Id
  stage: BookkeepingStage
  workItems: SearchableWorkItem[]
  accessStatus: BookkeepingAccessStatus
  cycles: BookkeepingCycle[]
  displayStage: BookkeepingDisplayStage
}

export const SearchableBookkeepingProgressionInfo = {
  default: {
    id: Id.none,
    stage: 'Unspecified',
    workItems: [],
    accessStatus: 'Unspecified',
    cycles: [],
    displayStage: 'ReadyToStart',
  } as SearchableBookkeepingProgressionInfo,
}

/**
 *  The searchable job.
 */
export class SearchableJob extends Model<SearchableJob> {
  static readonly none = new SearchableJob()

  id = Id.none

  @observable accessor workspaceId = Id.none

  @observable accessor conversationId = Id.none

  @observable accessor legendName = ''

  @observable accessor name = ''

  @observable accessor description = ''

  @observable accessor shortId = ''

  @observable accessor status: JobStatus = 'UnderConstruction'

  @observable accessor lastActivityAt: Date = unixEpoch()

  @observable accessor completedAt: Date | null = null

  @observable accessor members: CollaboratorProps[] = []

  @observable accessor legend: LegendSettingsProps = LegendSettings.none

  @observable accessor progression = JobProgression.none

  @observable accessor progressionStep: ProgressionStepStatus = 'NotStarted'

  @observable accessor isExtensionOnlyJob = false

  @observable accessor total = 0

  @observable accessor consultation: SearchableConsultation =
    SearchableConsultation.default

  @observable
  accessor bookkeepingProgressionInfo: SearchableBookkeepingProgressionInfo =
    SearchableBookkeepingProgressionInfo.default

  @observable accessor jobScopeChangeStatus: JobScopeChangeStatus =
    'Unspecified'

  /**
   * Display name.
   */
  @computed
  get displayName() {
    return this.name || this.description || this.legendName
  }

  /**
   * All the job progression steps.
   */
  @computed
  get allSteps() {
    const recordedSteps =
      this.progression?.recordedSteps.items.map((s) => s.step) ?? []

    return computeProgressionFlow({
      flow: this.progression.flow,
      steps: recordedSteps,
      isExtensionOnlyJob: this.isExtensionOnlyJob,
    })
  }

  /**
   * The current job progression step content.
   */
  @computed
  get currentStepContent() {
    return getCurrentStepContent(this.progression, this.allSteps)
  }

  /**
   * The champion client.
   */
  @computed
  get championClient() {
    return this.members.find(
      (c) => c.type === 'Client' && c.role === 'Champion',
    )
  }

  /**
   * The champion pro.
   */
  @computed
  get championPro() {
    return this.members.find((c) => c.type === 'Pro' && c.role === 'Champion')
  }
}
